import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Tippy from '@tippyjs/react';
import 'tippy.js/themes/light.css'; // must import any theme you wish to use with Tippy
import 'tippy.js/dist/tippy.css'; // optional
import { MutationStatus, useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { useService } from '@aesop-fables/containr-react';
import { Grid, Typography, useTheme } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { OffboardingWizard, offboardingWizardKey } from '../../services/offboarding';
import { ButtonFooter, Column, Row, Spacer, StepsHeader, UploadDropZone } from '../../components';
import { UpdateSignUpState } from '../../data/signUp/mutations';
import { SaveOrganizationLogo } from '../../data/organization/mutations';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { OrgSignUpStateData } from '../../models/SignUpData';
import { useLoading } from '../../hooks/useLoading';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';

const UploadLogo = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);
  const [logo, setLogo] = useState<Blob | undefined>(undefined);
  const [awaitingLogoSave, setAwaitingLogoSave] = useState(false);
  const appStorage = useAppStorage();
  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const offboardingWizard = useService<OffboardingWizard>(offboardingWizardKey);
  const updateSignUpState = useMutation(new UpdateSignUpState());
  const current = location.pathname;
  const { activeStep, totalSteps } = offboardingWizard.getStepInfo(current);
  const saveOrganizationLogo = useMutation(new SaveOrganizationLogo());
  const { offboardingComplete } = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  ) ?? { offboardingComplete: false };

  const navBack = () => {
    navigate('/');
  };

  const proceedToNextStep = async () => {
    await updateSignUpState.action({
      lastCompletedStep: current,
      offboardingComplete: offboardingComplete || activeStep === totalSteps,
    });
    const next = offboardingWizard.nextStep(location);
    navigate(next.route);
    setLoading(false);
  };

  const navToNext = async () => {
    setLoading(true);
    if (logo) {
      setAwaitingLogoSave(true);
      await saveOrganizationLogo.action(logo);
      return;
    }
    await proceedToNextStep();
  };

  useEffect(() => {
    if (!awaitingLogoSave) {
      return;
    }
    if (saveOrganizationLogo.error) {
      console.error('error saving logo:', saveOrganizationLogo.error);
      setToast(
        new Toast({
          severity: 'error',
          open: true,
          message: 'Error saving logo. Please try again.',
        }),
      );
      setLoading(false);
      setAwaitingLogoSave(false);
    } else if (saveOrganizationLogo.status === MutationStatus.Complete) {
      setAwaitingLogoSave(false);
      proceedToNextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveOrganizationLogo, awaitingLogoSave]);

  return (
    <Column
      style={{
        padding: '0px 100px 150px 100px',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Spacer height='xs' />
      <StepsHeader activeStep={activeStep} totalSteps={totalSteps} />
      <Spacer height='xs' />
      <Grid style={{ flexGrow: 1 }}>
        <Typography variant='p50Light' color='secondary'>
          Upload logo
        </Typography>
        <Spacer height='xs' />
        <Row style={{ alignItems: 'center' }}>
          <Typography variant='h2' color='info.main'>
            Upload a logo to customize your members’ experience. You can skip if you would
            like.&nbsp;
          </Typography>
          <Tippy placement='right' content={<LogoHelpText />} theme='tippy-rounded'>
            <HelpOutline style={{ color: theme.palette.info.main }} />
          </Tippy>
        </Row>
        <Spacer height='md' />
        <UploadDropZone setData={setLogo} uploadTypes={['logo']} />
      </Grid>

      <ButtonFooter
        primaryButton={{
          label: 'Next',
          onClick: navToNext,
          className: 'next',
        }}
        secondaryButton={{
          label: `${activeStep === 1 ? `Cancel` : `Back`}`,
          onClick: navBack,
          showCondition: true,
        }}
      />
    </Column>
  );
};

export const LogoHelpText = () => {
  return (
    <Column>
      <Typography variant='p18Bold' color='secondary.dark' style={{ paddingTop: '10px' }}>
        Logo upload details
      </Typography>
      <Spacer height='xxs' />
      <Typography
        variant='h2'
        color='secondary.dark'
        style={{ paddingLeft: '25px', paddingBottom: '10px' }}>
        <li>Must be a PNG or JPG file</li>
        <li>Dimensions must be 100px (height) by 400px (width)</li>
      </Typography>
    </Column>
  );
};

export default UploadLogo;
